import * as React from 'react';

type SelectOptionRow = {
    value: string;
    label: string;
};

type CpuEditForm = {
    vendor: string;
    model: string;
    releaseDate: string;
    socket: string;
    codename: string;
    coreConfigurationJson: string;
    baseClockFrequency: string;
    externalBusFrequency: string;
    voltage: string;
    tdpWatts: string;
};

type GpuEditForm = {
    vendor: string;
    model: string;
    releaseDate: string;
    interface: string;
};

type CpuCoreConfigurationRow = {
    tier: string;
    count: string;
    baseFrequency: string;
    boostFrequency: string;
    l1DataCachePerCore: string;
    l1InstructionCachePerCore: string;
    l2CachePerCore: string;
    l2CacheShared: string;
    l3CachePerCore: string;
    l3CacheShared: string;
    threadsPerCore: string;
};

type CpuCoreConfigurationPayload = {
    tier: string;
    count: number;
    baseFrequency: number;
    boostFrequency: number | null;
    l1DataCachePerCore: number;
    l1InstructionCachePerCore: number;
    l2CachePerCore: number;
    l2CacheShared: number;
    l3CachePerCore: number;
    l3CacheShared: number;
    threadsPerCore: number;
};

type EditCpuPage = {
    kind: 'cpu';
    heading: string;
    backUrl: string;
    viewUrl?: string | null;
    postUrl: string;
    submitLabel: string;
    deleteUrl?: string | null;
    deleteLabel?: string | null;
    deleteConfirmationMessage?: string | null;
    antiForgeryToken: string;
    errorMessage?: string | null;
    coreTierOptions: SelectOptionRow[];
    form: CpuEditForm;
};

type EditGpuPage = {
    kind: 'gpu';
    heading: string;
    backUrl: string;
    viewUrl?: string | null;
    postUrl: string;
    submitLabel: string;
    deleteUrl?: string | null;
    deleteLabel?: string | null;
    deleteConfirmationMessage?: string | null;
    antiForgeryToken: string;
    errorMessage?: string | null;
    interfaceOptions: SelectOptionRow[];
    form: GpuEditForm;
};

type HardwareEditPage = EditCpuPage | EditGpuPage;

type TextFieldProps = {
    label: string;
    name: string;
    value: string;
    type?: 'text' | 'number' | 'date';
    step?: string;
    required?: boolean;
    help?: React.ReactNode;
    onChange: (value: string) => void;
};

type SelectFieldProps = {
    label: string;
    name: string;
    value: string;
    options: SelectOptionRow[];
    required?: boolean;
    onChange: (value: string) => void;
};

function TextField({ label, name, value, type = 'text', step, required, help, onChange }: TextFieldProps) {
    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <input
                        name={name}
                        type={type}
                        value={value}
                        step={step}
                        required={required}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                    />
                </span>
            </label>
            {help ? <div className="field-help">{help}</div> : null}
        </div>
    );
}

function SelectField({ label, name, value, options, required, onChange }: SelectFieldProps) {
    return (
        <div className="form-field">
            <label>
                {label}
                <span className="field-control">
                    <select
                        name={name}
                        value={value}
                        required={required}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}>
                        <option value="">Select</option>
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </span>
            </label>
        </div>
    );
}

function createEmptyCpuTier(defaultTier: string): CpuCoreConfigurationRow {
    return {
        tier: defaultTier,
        count: '',
        baseFrequency: '',
        boostFrequency: '',
        l1DataCachePerCore: '',
        l1InstructionCachePerCore: '',
        l2CachePerCore: '',
        l2CacheShared: '0',
        l3CachePerCore: '',
        l3CacheShared: '0',
        threadsPerCore: ''
    };
}

function getObjectStringValue(source: Record<string, unknown>, camelName: string, pascalName: string): string {
    const value = source[camelName] ?? source[pascalName];
    return typeof value === 'string' ? value : '';
}

function getObjectNumberValue(source: Record<string, unknown>, camelName: string, pascalName: string): string {
    const value = source[camelName] ?? source[pascalName];
    return typeof value === 'number' ? value.toString() : '';
}

function getObjectNullableNumberValue(source: Record<string, unknown>, camelName: string, pascalName: string): string {
    const value = source[camelName] ?? source[pascalName];
    return typeof value === 'number' ? value.toString() : '';
}

function parseCpuCoreConfigurationRows(json: string, defaultTier: string): CpuCoreConfigurationRow[] {
    if (json.trim().length === 0) {
        return [createEmptyCpuTier(defaultTier)];
    }

    try {
        const parsed = JSON.parse(json) as unknown[];
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [createEmptyCpuTier(defaultTier)];
        }

        return parsed.map((item) => {
            const source = typeof item === 'object' && item !== null ? item as Record<string, unknown> : {};

            return {
                tier: getObjectStringValue(source, 'tier', 'Tier') || defaultTier,
                count: getObjectNumberValue(source, 'count', 'Count'),
                baseFrequency: getObjectNumberValue(source, 'baseFrequency', 'BaseFrequency'),
                boostFrequency: getObjectNullableNumberValue(source, 'boostFrequency', 'BoostFrequency'),
                l1DataCachePerCore: getObjectNumberValue(source, 'l1DataCachePerCore', 'L1DataCachePerCore'),
                l1InstructionCachePerCore: getObjectNumberValue(source, 'l1InstructionCachePerCore', 'L1InstructionCachePerCore'),
                l2CachePerCore: getObjectNumberValue(source, 'l2CachePerCore', 'L2CachePerCore'),
                l2CacheShared: getObjectNumberValue(source, 'l2CacheShared', 'L2CacheShared') || '0',
                l3CachePerCore: getObjectNumberValue(source, 'l3CachePerCore', 'L3CachePerCore'),
                l3CacheShared: getObjectNumberValue(source, 'l3CacheShared', 'L3CacheShared') || '0',
                threadsPerCore: getObjectNumberValue(source, 'threadsPerCore', 'ThreadsPerCore')
            };
        });
    }
    catch {
        return [createEmptyCpuTier(defaultTier)];
    }
}

function parseRequiredInt(value: string): number | null {
    if (value.trim().length === 0) {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
}

function parseOptionalInt(value: string): number | null | undefined {
    if (value.trim().length === 0) {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : undefined;
}

function buildCpuCoreConfigurationJson(rows: CpuCoreConfigurationRow[], validTiers: Set<string>): { value: string; error: string | null; } {
    if (rows.length === 0) {
        return { value: '', error: 'At least one CPU core tier is required.' };
    }

    const payload: CpuCoreConfigurationPayload[] = [];
    let hasNonDisabledTier = false;

    for (const [index, row] of rows.entries()) {
        const segmentIndex = index + 1;

        if (!validTiers.has(row.tier)) {
            return { value: '', error: `Tier ${segmentIndex} has invalid core tier.` };
        }

        const count = parseRequiredInt(row.count);
        if (count === null || count <= 0) {
            return { value: '', error: `Tier ${segmentIndex} has invalid core count.` };
        }

        const baseFrequency = parseRequiredInt(row.baseFrequency);
        if (baseFrequency === null || baseFrequency <= 0) {
            return { value: '', error: `Tier ${segmentIndex} has invalid base frequency.` };
        }

        const boostFrequency = parseOptionalInt(row.boostFrequency);
        if (boostFrequency === undefined || (boostFrequency !== null && boostFrequency <= 0)) {
            return { value: '', error: `Tier ${segmentIndex} has invalid boost frequency.` };
        }

        const l1DataCachePerCore = parseRequiredInt(row.l1DataCachePerCore);
        const l1InstructionCachePerCore = parseRequiredInt(row.l1InstructionCachePerCore);
        const l2CachePerCore = parseRequiredInt(row.l2CachePerCore);
        const l2CacheShared = parseRequiredInt(row.l2CacheShared);
        const l3CachePerCore = parseRequiredInt(row.l3CachePerCore);
        const l3CacheShared = parseRequiredInt(row.l3CacheShared);
        const threadsPerCore = parseRequiredInt(row.threadsPerCore);

        if (l1DataCachePerCore === null || l1DataCachePerCore < 0
            || l1InstructionCachePerCore === null || l1InstructionCachePerCore < 0
            || l2CachePerCore === null || l2CachePerCore < 0
            || l2CacheShared === null || l2CacheShared < 0
            || l3CachePerCore === null || l3CachePerCore < 0
            || l3CacheShared === null || l3CacheShared < 0) {
            return { value: '', error: `Tier ${segmentIndex} has invalid cache value.` };
        }

        if (threadsPerCore === null || threadsPerCore <= 0 || threadsPerCore > 255) {
            return { value: '', error: `Tier ${segmentIndex} has invalid thread count.` };
        }

        if (row.tier !== 'Disabled') {
            hasNonDisabledTier = true;
        }

        payload.push({
            tier: row.tier,
            count,
            baseFrequency,
            boostFrequency,
            l1DataCachePerCore,
            l1InstructionCachePerCore,
            l2CachePerCore,
            l2CacheShared,
            l3CachePerCore,
            l3CacheShared,
            threadsPerCore
        });
    }

    if (!hasNonDisabledTier) {
        return { value: '', error: 'CPU core configuration must include at least one non-disabled core tier.' };
    }

    return {
        value: JSON.stringify(payload),
        error: null
    };
}

function CpuCoreTierEditor(
    {
        rows,
        options,
        error,
        onChange,
        onAdd,
        onRemove,
        onMove
    }: {
        rows: CpuCoreConfigurationRow[];
        options: SelectOptionRow[];
        error: string | null;
        onChange: (rowIndex: number, field: keyof CpuCoreConfigurationRow, value: string) => void;
        onAdd: () => void;
        onRemove: (rowIndex: number) => void;
        onMove: (fromIndex: number, toIndex: number) => void;
    }) {
    const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

    return (
        <div className="form-field hardware-tier-editor">
            <label>Core configuration</label>
            {rows.map((row, rowIndex) => (
                <div
                    key={`${row.tier}-${rowIndex}`}
                    className={`card hardware-tier-card${draggedIndex === rowIndex ? ' hardware-tier-card--dragging' : ''}`}
                    draggable
                    onDragStart={() => setDraggedIndex(rowIndex)}
                    onDragEnd={() => setDraggedIndex(null)}
                    onDragOver={(event: React.DragEvent<HTMLDivElement>) => event.preventDefault()}
                    onDrop={(event: React.DragEvent<HTMLDivElement>) => {
                        event.preventDefault();
                        if (draggedIndex !== null && draggedIndex !== rowIndex) {
                            onMove(draggedIndex, rowIndex);
                        }

                        setDraggedIndex(null);
                    }}>
                    <div className="hardware-tier-card__header">
                        <div>
                            <strong>Tier {rowIndex + 1}</strong>
                        </div>
                        <div className="row button-row">
                            <button type="button" className="hardware-tier-card__drag-handle">Drag</button>
                            <button type="button" onClick={() => onRemove(rowIndex)} disabled={rows.length === 1}>Remove tier</button>
                        </div>
                    </div>
                    <div className="hardware-tier-card__section">
                        <h3>Topology</h3>
                        <div className="row hardware-tier-card__grid">
                            <label>
                                Tier
                                <select value={row.tier} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onChange(rowIndex, 'tier', event.target.value)}>
                                    {options.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                Count
                                <input type="number" step="1" min="1" value={row.count} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'count', event.target.value)} />
                            </label>
                            <label>
                                Threads/core
                                <input type="number" step="1" min="1" max="255" value={row.threadsPerCore} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'threadsPerCore', event.target.value)} />
                            </label>
                        </div>
                    </div>
                    <div className="hardware-tier-card__section">
                        <h3>Frequencies</h3>
                        <div className="row hardware-tier-card__grid">
                            <label>
                                Base MHz
                                <input type="number" step="1" min="1" value={row.baseFrequency} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'baseFrequency', event.target.value)} />
                            </label>
                            <label>
                                Boost MHz
                                <input type="number" step="1" min="1" value={row.boostFrequency} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'boostFrequency', event.target.value)} />
                            </label>
                        </div>
                    </div>
                    <div className="hardware-tier-card__section">
                        <h3>Caches</h3>
                        <div className="row hardware-tier-card__grid">
                            <label>
                                L1D/core (KB)
                                <input type="number" step="1" min="0" value={row.l1DataCachePerCore} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l1DataCachePerCore', event.target.value)} />
                            </label>
                            <label>
                                L1I/core (KB)
                                <input type="number" step="1" min="0" value={row.l1InstructionCachePerCore} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l1InstructionCachePerCore', event.target.value)} />
                            </label>
                            <label>
                                L2/core (KB)
                                <input type="number" step="1" min="0" value={row.l2CachePerCore} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l2CachePerCore', event.target.value)} />
                            </label>
                            <label>
                                Shared L2 (KB)
                                <input type="number" step="1" min="0" value={row.l2CacheShared} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l2CacheShared', event.target.value)} />
                            </label>
                            <label>
                                L3/core (KB)
                                <input type="number" step="1" min="0" value={row.l3CachePerCore} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l3CachePerCore', event.target.value)} />
                            </label>
                            <label>
                                Shared L3 (KB)
                                <input type="number" step="1" min="0" value={row.l3CacheShared} onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(rowIndex, 'l3CacheShared', event.target.value)} />
                            </label>
                        </div>
                    </div>
                </div>
            ))}
            <div className="row button-row">
                <button type="button" onClick={onAdd}>Add tier</button>
            </div>
            {error ? <div className="field-help">{error}</div> : null}
        </div>
    );
}

function CpuForm({ page }: { page: EditCpuPage }) {
    const [form, setForm] = React.useState<CpuEditForm>(page.form);
    const defaultTier = page.coreTierOptions[0]?.value ?? 'PCore';
    const [coreRows, setCoreRows] = React.useState<CpuCoreConfigurationRow[]>(() => parseCpuCoreConfigurationRows(page.form.coreConfigurationJson, defaultTier));
    const [submitAttempted, setSubmitAttempted] = React.useState(false);
    const validTiers = React.useMemo(() => new Set(page.coreTierOptions.map((option) => option.value)), [page.coreTierOptions]);

    const coreConfigurationResult = React.useMemo(() => buildCpuCoreConfigurationJson(coreRows, validTiers), [coreRows, validTiers]);

    const moveCoreRow = React.useCallback((fromIndex: number, toIndex: number) => {
        setCoreRows((current) => {
            const next = [...current];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);

    return (
        <form
            action={page.postUrl}
            method="post"
            onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                setSubmitAttempted(true);
                if (coreConfigurationResult.error !== null) {
                    event.preventDefault();
                }
            }}>
            <input type="hidden" name="__RequestVerificationToken" value={page.antiForgeryToken} />
            <input type="hidden" name="CoreConfigurationJson" value={coreConfigurationResult.value} />

            {page.errorMessage ? <div className="alert">{page.errorMessage}</div> : null}

            <TextField label="Vendor" name="Vendor" value={form.vendor} required onChange={(vendor) => setForm((current) => ({ ...current, vendor }))} />
            <TextField label="Model" name="Model" value={form.model} required onChange={(model) => setForm((current) => ({ ...current, model }))} />
            <TextField label="Release date" name="ReleaseDate" value={form.releaseDate} type="date" required onChange={(releaseDate) => setForm((current) => ({ ...current, releaseDate }))} />
            <TextField label="Socket" name="Socket" value={form.socket} onChange={(socket) => setForm((current) => ({ ...current, socket }))} />
            <TextField label="Codename" name="Codename" value={form.codename} onChange={(codename) => setForm((current) => ({ ...current, codename }))} />
            <CpuCoreTierEditor
                rows={coreRows}
                options={page.coreTierOptions}
                error={submitAttempted ? coreConfigurationResult.error : null}
                onChange={(rowIndex, field, value) => setCoreRows((current) => current.map((row, index) => index === rowIndex ? { ...row, [field]: value } : row))}
                onAdd={() => setCoreRows((current) => [...current, createEmptyCpuTier(defaultTier)])}
                onRemove={(rowIndex) => setCoreRows((current) => current.filter((_, index) => index !== rowIndex))}
                onMove={moveCoreRow}
            />
            <TextField label="Base clock frequency (MHz)" name="BaseClockFrequency" value={form.baseClockFrequency} type="number" step="1" required onChange={(baseClockFrequency) => setForm((current) => ({ ...current, baseClockFrequency }))} />
            <TextField label="External bus frequency (MHz)" name="ExternalBusFrequency" value={form.externalBusFrequency} type="number" step="1" onChange={(externalBusFrequency) => setForm((current) => ({ ...current, externalBusFrequency }))} />
            <TextField label="Voltage (V)" name="Voltage" value={form.voltage} type="number" step="any" onChange={(voltage) => setForm((current) => ({ ...current, voltage }))} />
            <TextField label="TDP (W)" name="TdpWatts" value={form.tdpWatts} type="number" step="1" required onChange={(tdpWatts) => setForm((current) => ({ ...current, tdpWatts }))} />

            <button type="submit">{page.submitLabel}</button>
        </form>
    );
}

function GpuForm({ page }: { page: EditGpuPage }) {
    const [form, setForm] = React.useState<GpuEditForm>(page.form);

    return (
        <form action={page.postUrl} method="post">
            <input type="hidden" name="__RequestVerificationToken" value={page.antiForgeryToken} />

            {page.errorMessage ? <div className="alert">{page.errorMessage}</div> : null}

            <TextField label="Vendor" name="Vendor" value={form.vendor} required onChange={(vendor) => setForm((current) => ({ ...current, vendor }))} />
            <TextField label="Model" name="Model" value={form.model} required onChange={(model) => setForm((current) => ({ ...current, model }))} />
            <TextField label="Release date" name="ReleaseDate" value={form.releaseDate} type="date" required onChange={(releaseDate) => setForm((current) => ({ ...current, releaseDate }))} />
            <SelectField label="Interface" name="Interface" value={form.interface} options={page.interfaceOptions} required onChange={(value) => setForm((current) => ({ ...current, interface: value }))} />

            <button type="submit">{page.submitLabel}</button>
        </form>
    );
}

export function HardwareEdit({ page }: { page: HardwareEditPage }) {
    if (page.kind === 'cpu') {
        return <CpuForm page={page} />;
    }

    return <GpuForm page={page} />;
}
